import React from 'react'

import type { SchemaGroup } from '@/__generated/es-api'
import { Form } from '@/app/component/form'
import { ActionsContainer, FormContainer } from '@/app/component/form/common'
import SubmitButton from '@/app/component/form/submit-button'
import { GROUPS_DEFAULT_FIELD_CHART_LIMIT } from '@/app/module/contacts/definitions'
import { operands } from '@/app/module/groups/definitions'
import { useCreateGroup } from '@/app/module/groups/hooks/use-create-group'
import { PersonalizationType } from '@/app/types'

import { fields, segmentSchema } from './specs'
import { toConditions, toConditionsFormValue } from './utils'

type Props = {
  personalizationList: PersonalizationType
  sourceSegment?: Pick<SchemaGroup, 'name' | 'conditions'>

  onExit: () => void
}

const CreateSegmentForm: React.FC<Props> = ({ personalizationList, sourceSegment, onExit }) => {
  const [createGroup, { isLoading }] = useCreateGroup()
  const initialValues = React.useMemo(
    () =>
      sourceSegment
        ? {
            name: `Copy of ${sourceSegment.name}`.slice(0, GROUPS_DEFAULT_FIELD_CHART_LIMIT),
            conditions: toConditionsFormValue(sourceSegment.conditions),
          }
        : defaultValues,
    [sourceSegment],
  )

  const handleSubmit = React.useCallback(
    async (values: { name: string; conditions: string }) => {
      const success = await createGroup({
        conditions: toConditions(values.conditions),
        type: 'SMART',
        name: values.name,
        description: '',
        noAutodelete: false,
      })
      if (!success) {
        return
      }
      onExit()
    },
    [createGroup, onExit],
  )

  const segmentFields = React.useMemo(
    () =>
      ({
        ...fields,
        conditions: {
          fieldProps: { label: 'Clauses', name: 'conditions', operands, personalizationList },
          type: 'conditions',
        },
      }) as const,
    [personalizationList],
  )

  return (
    <FormContainer>
      <Form
        defaultValues={initialValues}
        fields={segmentFields}
        name="create-segment"
        schema={segmentSchema}
        shouldReset
        onSubmit={handleSubmit}
        renderActions={({ isDirty }) => (
          <ActionsContainer>
            <SubmitButton isDirty={!!sourceSegment || isDirty} isSubmitting={isLoading}>
              Save
            </SubmitButton>
          </ActionsContainer>
        )}
      />
    </FormContainer>
  )
}

const defaultValues = {
  name: '',
  conditions: toConditionsFormValue(),
}

export default CreateSegmentForm
